<template>

  <v-container fluid class="page page-receiver-org-item-edit">

    <!-- Breadcrumb -->
    <v-row class="mt-4">
      <v-col cols="12" class="d-flex align-center justify-start page-breadcrumb-new">
        <!-- (breadcrumb) -->
        <v-icon small class="mr-2">mdi-account-circle-outline</v-icon>
        <router-link :to="{ name: 'receiver-org-list' }">Receiver Orgs</router-link>
        <div class="separator">/</div>
        <div class="active-item" v-if="orgToEdit"><span class="label-prefix">Edit Org:</span> {{ activeOrg.name }}</div>
      </v-col>
    </v-row>

    <!-- Page Actions (close, cancel, save) -->
    <v-row class="mt-10 mb-4">
      <v-col cols="6" class="pl-8 pt-0">
        <v-btn
            v-if="isDirty"
            color="gray"
            width="100"
            :disabled="isOrgInfoSaving"
            @click="cancel">
          Cancel
        </v-btn>
        <div class="changed-message-text" v-else-if="orgInfoSavedMessage">
          <v-icon color="green" class="mr-2 pb-1">mdi-check-bold</v-icon>
          <span>{{ orgInfoSavedMessage }}</span>
        </div>
      </v-col>
      <v-col cols="6" class="pr-8 pt-0" align="right">
        <v-btn
            v-if="isDirty"
            color="primary"
            width="110"
            :disabled="isOrgInfoSaving"
            @click="saveOrgInfo">
          Save
        </v-btn>
        <v-btn
            v-else
            color="gray"
            width="110"
            @click="close">
          Close
        </v-btn>
      </v-col>
    </v-row>

    <!-- (data saving) -->
    <template v-if="isOrgInfoSaving">
      <v-row>
        <v-col class="mt-4 d-flex align-center justify-center" style="height:120px">
          <v-progress-circular indeterminate :size="50" style="margin:20px 0" color="primary" />
        </v-col>
      </v-row>
    </template>

    <template v-else>
      <!-- Edit Org Info -->
      <v-container fluid class="pt-3 pb-9 page-content" v-if="orgToEdit">
        <!-- Org info title -->
        <v-row class="pa-0 ma-0">
          <v-col cols="12">
            <span class="title">Org Info</span>
          </v-col>
        </v-row>

        <!-- First name -->
        <v-row align="center" class="pa-0 ma-0 mt-4">
          <v-col cols="12" xs="12" sm="3" md="3" lg="1">
            <span class="label">Org name:</span>
          </v-col>
          <v-col cols="12" xs="12" sm="8" md="5" lg="5">
            <v-text-field
                outlined
                hide-details
                :disabled="isOrgInfoSaving"
                :rules="$root.rules.fieldRequired"
                @input="setDirty('name')"
                v-model.trim="orgToEdit.name" />
          </v-col>
        </v-row>
      </v-container>

      <!-- Edit Registration Info -->
      <v-container fluid class="mt-8 pt-3 pb-9 page-content" v-if="orgRegistrationToEdit">
        <!-- Org info title -->
        <v-row class="pa-0 ma-0">
          <v-col cols="12">
            <span class="title">Org Registration</span>
          </v-col>
        </v-row>

        <!-- Registration status message -->
        <v-row align="center" class="pa-0 ma-0 mt-4">
          <v-col cols="12" v-if="registrationStatusMessage">
            <div :class="(orgRegistrationToEdit.status === 'active') ? 'registration-status-active' : 'registration-status-inactive'">
              {{ registrationStatusMessage }}
            </div>
          </v-col>
        </v-row>

        <!-- Owner email -->
        <v-row align="center" class="pa-0 ma-0 mt-1">
          <v-col cols="12" xs="12" sm="3" md="3" lg="1">
            <span class="label">Email:</span>
          </v-col>
          <v-col cols="12" xs="12" sm="8" md="6" lg="5">
            <v-text-field
                outlined
                hide-details
                :disabled="isOrgInfoSaving"
                @input="setDirty('email')"
                v-model.trim="orgRegistrationToEdit.email" />
          </v-col>
        </v-row>

        <!-- Registration status -->
        <v-row align="center" class="pa-0 ma-0 mt-2" v-if="orgRegistrationToEdit.status === 'active'">
          <v-col cols="12" xs="12" sm="3" md="3" lg="1">
            <div class="label">Expires:</div>
          </v-col>
          <v-col cols="12" xs="12" sm="9" md="5" lg="4">
            <div v-if="orgRegistrationToEdit.expires_at">{{ formatDateTime(orgRegistrationToEdit.expires_at) }}</div>
            <div v-else>Never</div>
          </v-col>
        </v-row>
      </v-container>
    </template>

  </v-container>

</template>

<script>
export default {
  name: 'ReceiverOrgItemEditPage',

  props: {
    orgId: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      // ------------ manage org info
      isDataLoading: false,
      activeOrg: null,
      orgToEdit: null,
      isDirty: false,
      dirtyProps: [],
      isOrgInfoSaving: false,
      orgInfoSaveErrorMessage: null,
      orgInfoSavedMessage: null,
      // ------------ manage org registration
      activeRegistration: null,
      orgRegistrationToEdit: null
    }
  },

  computed: {
    uiType () {
      return this.$root.uiType()
    },
    registrationStatusMessage () {
      switch (this.orgRegistrationToEdit?.status) {
        case 'active': return 'This registration is still valid.'
        case 'expired': return 'This registration has expired.'
        case 'canceled': return 'This registration has been cancelled.'
        default: return null
      }
    }
  },

  mounted () {
    // Load receiver org to edit
    this.loadOrgToEdit()
  },

  beforeDestroy () {
    // Clear org editing
    this.orgToEdit = null
    this.orgRegistrationToEdit = null
  },

  methods: {
    // Manage dirty state
    setDirty (propName) {
      if (propName === 'email' && this.activeRegistration[propName] !== this.orgRegistrationToEdit[propName]) {
        if (!this.$root.utils.includes(this.dirtyProps, propName)) this.dirtyProps.push(propName)
        this.isDirty = true
      } else if (this.activeOrg[propName] !== this.orgToEdit[propName]) {
        if (!this.$root.utils.includes(this.dirtyProps, propName)) this.dirtyProps.push(propName)
        this.isDirty = true
      } else {
        const dirtyPropIndex = this.dirtyProps.findIndex(prop => prop === propName)
        if (dirtyPropIndex !== -1) this.dirtyProps.splice(dirtyPropIndex, 1)
        if (this.dirtyProps.length === 0) this.isDirty = false
      }
    },

    // Display a date/time
    formatDateTime (date) {
      return (date) ? this.$root.utils.format(date, 'YYYY/MM/DD h:mm a') : ''
    },

    // Cancel changes
    cancel () {
      this.orgToEdit = this.$root.utils.cloneDeep(this.activeOrg) // reset user info
      this.dirtyProps = []
      this.isDirty = false
    },

    // Return to receiver org page
    close () {
      this.$root.goTo('receiver-org-item', { orgId: this.orgId })
    },

    async loadOrgToEdit () {
      this.isDataLoading = true

      try {
        // Load active org and registration info
        this.activeOrg = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `receiver-org/${this.orgId}`,
          opts: {
            params: {
              with: 'registration_code'
            }
          }
        })
        this.activeRegistration = this.activeOrg.registration_code

        // Load org data for editing
        this.orgToEdit = this.$root.utils.cloneDeep(this.activeOrg)
        if (this.orgToEdit.registration_code.status !== 'claimed') this.orgRegistrationToEdit = this.orgToEdit.registration_code
      } catch (err) {
        console.error('Error loading receiver org:', err)
      } finally {
        this.isDataLoading = false
      }
    },

    // Save org info changes
    async saveOrgInfo () {
      this.isOrgInfoSaving = true

      try {
        // Update org
        if (this.$root.utils.includes(this.dirtyProps, 'name')) {
          await this.$store.dispatch('sh/dataApi', {
            method: 'post',
            uri: `receiver-org/${this.orgId}`,
            opts: {
              bodyData: {
                name: this.orgToEdit.name
              }
            }
          })
        }

        // Update org registration
        if (this.$root.utils.includes(this.dirtyProps, 'email')) {
          await this.$store.dispatch('sh/dataApi', {
            method: 'post',
            uri: `org-registration/${this.orgRegistrationToEdit.id}`,
            opts: {
              bodyData: {
                email: this.orgRegistrationToEdit.email
              }
            }
          })
        }

        // Refresh org data
        this.loadOrgToEdit()

        // Clear dirty
        this.dirtyProps = []
        this.isDirty = false

        // Flash saved message
        this.orgInfoSavedMessage = 'Changes Saved!'
        setTimeout(() => {
          this.orgInfoSavedMessage = null
        }, 4000)
      } catch (err) {
        this.orgInfoSaveErrorMessage = err.message || err.text || err
      } finally {
        this.isOrgInfoSaving = false
      }
    }
  }
}
</script>

<style>
  .page-receiver-org-item-edit .changed-message-text {
    font-size: 1.0rem;
    color: #4CAF50;
    font-weight: 400;
  }

  .page-receiver-org-item-edit .registration-status-active {
    font-size: 1.1rem;
    color: #4CAF50;
    font-weight: 400;
  }

  .page-receiver-org-item-edit .registration-status-inactive {
    font-size: 1.1rem;
    color: #F44336;
    font-weight: 400;
  }
</style>
